import React, { useEffect, useState } from "react";
import { ExternalLink, Loader2, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/datetime";

/**
 * Carte « Révision du travail en cours ».
 *
 * - Côté client : visible uniquement si `revisionLink` est renseigné.
 * - Côté admin : toujours visible, avec un petit formulaire pour renseigner /
 *   modifier / effacer le lien (ex. partage InDesign, Figma, etc.).
 */
export default function RevisionLinkCard({
  orderId,
  revisionLink,
  revisionLinkUpdatedAt,
  isAdmin,
  onUpdated,
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(revisionLink || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setValue(revisionLink || "");
  }, [revisionLink]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${orderId}/revision-link`, {
        revision_link: value.trim() || null,
      });
      toast.success(value.trim() ? "Lien de révision enregistré" : "Lien de révision retiré");
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Côté client : rien si pas de lien
  if (!isAdmin && !revisionLink) return null;

  return (
    <div className="cs-card p-6" data-testid="revision-link-card">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="cs-eyebrow text-[#0E7C90]">Révision du travail en cours</div>
        {revisionLinkUpdatedAt && (
          <div className="text-[11px] font-mono text-neutral-500">
            Dernière mise à jour · {fmtDateTime(revisionLinkUpdatedAt)}
          </div>
        )}
      </div>

      {revisionLink && !editing && (
        <>
          <p className="mt-3 text-sm leading-relaxed text-neutral-700">
            Consultez la création en cours et ajoutez directement vos remarques
            sur le document.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <a
              href={revisionLink}
              target="_blank"
              rel="noopener noreferrer"
              className="cs-btn-primary inline-flex items-center gap-2"
              data-testid="revision-link-open"
            >
              <ExternalLink size={14} /> Ouvrir l'espace de révision
            </a>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1 px-3 py-2 border border-black/25 text-sm hover:border-[#0E7C90]"
                data-testid="revision-link-edit"
              >
                <Pencil size={13} /> Modifier le lien
              </button>
            )}
          </div>
        </>
      )}

      {isAdmin && (!revisionLink || editing) && (
        <div className="mt-4 space-y-3">
          {!revisionLink && !editing && (
            <p className="text-sm text-neutral-600">
              Collez ici l'URL de partage InDesign (ou tout autre espace de
              relecture en ligne). Le client verra alors apparaître une carte
              dédiée dans son espace projet.
            </p>
          )}
          <input
            type="url"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="https://indd.adobe.com/view/…"
            className="cs-input"
            data-testid="revision-link-input"
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="cs-btn-primary inline-flex items-center gap-2"
              data-testid="revision-link-save"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}
              Enregistrer
            </button>
            {editing && (
              <button
                type="button"
                onClick={() => {
                  setValue(revisionLink || "");
                  setEditing(false);
                }}
                className="px-3 py-2 border border-black/25 text-sm hover:border-black/60"
                data-testid="revision-link-cancel"
              >
                Annuler
              </button>
            )}
            {revisionLink && editing && (
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm("Retirer le lien de révision ? Il disparaîtra côté client.")) return;
                  setValue("");
                  setBusy(true);
                  try {
                    await api.patch(`/admin/orders/${orderId}/revision-link`, { revision_link: null });
                    toast.success("Lien retiré");
                    setEditing(false);
                    onUpdated?.();
                  } catch (e) {
                    toast.error(formatApiError(e));
                  } finally {
                    setBusy(false);
                  }
                }}
                className="px-3 py-2 border border-red-300 text-red-600 text-sm hover:bg-red-50"
                data-testid="revision-link-remove"
              >
                Retirer le lien
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
